/**
 * Every date on this site refers to a Malaysian moment — a meeting time, a
 * sign-in, a publication date — so they are all rendered in Kuala Lumpur time.
 *
 * The zone is pinned rather than taken from the viewer's browser on purpose.
 * A church contact reading this from overseas, or anyone whose machine is set
 * to another region, must still see the same time the training office would
 * read off the same screen. "9:00 am" has to mean 9:00 am in Malaysia.
 *
 * Every timestamp column is TIMESTAMPTZ and the API returns raw ISO instants,
 * so this is the single place the conversion happens.
 */
export const TIME_ZONE = 'Asia/Kuala_Lumpur';

/** Malaysia is UTC+8 year round — no daylight saving — so this never shifts. */
export const TIME_ZONE_LABEL = 'MYT';

const LOCALE = 'en-GB';

// Built once: constructing an Intl formatter is comparatively expensive, and
// these are called per row in admin tables.
const dateOnly = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TIME_ZONE,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const dateAndTime = new Intl.DateTimeFormat(LOCALE, {
  timeZone: TIME_ZONE,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

const yearOnly = new Intl.DateTimeFormat(LOCALE, { timeZone: TIME_ZONE, year: 'numeric' });

/** Null, empty and unparseable values all collapse to the fallback. */
function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** e.g. "15 Sep 2026" */
export function formatDate(value: string | Date | null | undefined, fallback = '—'): string {
  const parsed = toDate(value);
  return parsed ? dateOnly.format(parsed) : fallback;
}

/** e.g. "15 Sep 2026, 11:02 am" */
export function formatDateTime(value: string | Date | null | undefined, fallback = '—'): string {
  const parsed = toDate(value);
  return parsed ? dateAndTime.format(parsed) : fallback;
}

/** Same as formatDateTime with the zone spelled out — for tooltips. */
export function formatDateTimeWithZone(
  value: string | Date | null | undefined,
  fallback = '—',
): string {
  const parsed = toDate(value);
  return parsed ? `${dateAndTime.format(parsed)} (${TIME_ZONE_LABEL})` : fallback;
}

/** The current year in Kuala Lumpur, which is what a copyright line means. */
export const currentYear = (): string => yearOnly.format(new Date());
